import axios from 'axios';
import { supabase } from '../lib/supabase';

const API = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`,
  timeout: 120000, // 2 min — job search with AI can take a while
  headers: {
    // Legacy fallback — kept while we transition to JWT. Can be removed from
    // Vercel env after V1-5 is fully validated in prod.
    'X-API-Key': process.env.REACT_APP_API_KEY || '',
  },
});

// Request interceptor: inject the Supabase JWT (Bearer token) on every request.
// Reads the current session at request-time, not at module-load-time, so we
// always send the freshest token (including after refresh).
API.interceptors.request.use(async (config) => {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (e) {
    // Non-fatal — fall back to X-API-Key header
    // eslint-disable-next-line no-console
    console.warn('Could not read Supabase session for auth header:', e);
  }

  // V1-10: Anthropic key is no longer sent from the client. The backend now
  // stores it encrypted at rest (table user_anthropic_keys), keyed by JWT
  // sub. Endpoints that need Claude resolve the key server-side. The
  // client only manages connect/disconnect via /user/anthropic-key.

  return config;
});

// Response interceptor: transform HTTP errors into friendly messages
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response) {
      // Network error — backend not running
      error.friendlyMessage = 'Cannot reach the server. Make sure the backend is running on port 8000.';
    } else {
      const status = error.response.status;
      const detail = error.response.data?.detail;

      if (status === 400) {
        error.friendlyMessage = typeof detail === 'string'
          ? detail
          : 'Invalid request. Check your inputs.';
      } else if (status === 401) {
        // Session expired / invalid — sign out and bounce to login.
        // The AuthContext listener will pick up the state change.
        supabase.auth.signOut().catch(() => {});
        error.friendlyMessage = typeof detail === 'string'
          ? detail
          : 'Session expired. Please sign in again.';
      } else if (status === 403) {
        error.friendlyMessage = typeof detail === 'string'
          ? detail
          : "You don't have access to this resource.";
      } else if (status === 404) {
        error.friendlyMessage = typeof detail === 'string'
          ? detail
          : 'Resource not found.';
      } else if (status === 428) {
        // 428 Precondition Required — backend's signal that the user has no
        // Anthropic key on file. The gate component listens for this and
        // redirects to the key setup screen. Detail is a structured object
        // {code, message}.
        const code = typeof detail === 'object' ? detail?.code : null;
        const message = typeof detail === 'object'
          ? detail?.message
          : (typeof detail === 'string' ? detail : 'Anthropic key required.');
        error.friendlyMessage = message;
        error.requiresAnthropicKey = true;
        error.anthropicKeyErrorCode = code;
      } else if (status === 422) {
        error.friendlyMessage = 'Validation error. Check your inputs.';
      } else if (status === 500) {
        error.friendlyMessage = 'Server error. Check the backend logs for details.';
      } else {
        error.friendlyMessage = detail || `Unexpected error (${status}).`;
      }
    }
    return Promise.reject(error);
  }
);

// Applications
export const getApplications = () => API.get('/applications');
export const createApplication = (data) => API.post('/applications', data);
export const updateApplication = (id, data) => API.patch(`/applications/${id}`, data);
export const deleteApplication = (id) => API.delete(`/applications/${id}`);

// Jobs
export const getJobs = () => API.get('/jobs');
// Job search kicks off N Claude calls (one per scored job). Even with
// parallelism on the backend, a fresh search over 15-20 roles can easily
// take 60-90s. Give the endpoint its own longer timeout so the browser
// doesn't give up while the backend is still working.
export const searchJobs = (params) =>
  API.post('/jobs/search', params, { timeout: 240000 /* 4 min */ });
export const applyToJob = (id) => API.post(`/jobs/${id}/apply`);

// Resume
export const uploadResume = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post('/resume/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getProfile = () => API.get('/resume/profile');
export const deleteResume = () => API.delete('/resume');

// Tailored resume
export const getTailoredResume = (appId) =>
  API.get(`/applications/${appId}/tailor`);
export const tailorResume = (appId, regenerate = false) =>
  API.post(`/applications/${appId}/tailor`, null, { params: { regenerate } });
export const downloadTailoredResumeUrl = (appId) =>
  `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/applications/${appId}/tailor/download`;
export const downloadTailoredResume = (appId) =>
  API.get(`/applications/${appId}/tailor/download`, { responseType: 'blob' });

// Gmail
export const getGmailStatus = () => API.get('/gmail/status');
export const getGmailAuthUrl = () => API.get('/gmail/auth');
export const syncGmail = () => API.post('/gmail/sync');

// Stats
export const getStats = () => API.get('/stats');

// API usage (internal — not linked from Navbar)
// Hybrid: /usage returns our own counters; /usage/serpapi + /usage/anthropic
// proxy to provider endpoints for live data.
export const getApiUsage = () => API.get('/usage');
export const getSerpapiUsage = () => API.get('/usage/serpapi');
export const getAnthropicUsage = () => API.get('/usage/anthropic');

// User Anthropic key (V1-10) — encrypted server-side storage
//
// getKeyStatus() returns metadata only:
//   { has_key, last_4, validated_at, last_validation_error }
// saveKey() validates with Anthropic before persisting; if invalid, returns
//   { ok: false, error } and stores nothing.
// deleteKey() is idempotent.
export const getKeyStatus = () => API.get('/user/anthropic-key');
export const saveKey = (apiKey) =>
  API.post('/user/anthropic-key', { api_key: apiKey });
export const deleteKey = () => API.delete('/user/anthropic-key');
// Stateless validation — used during onboarding before user commits to saving.
export const validateKey = (apiKey) =>
  API.post('/validate-key', { api_key: apiKey });

// Onboarding state (V1-11 — state-aware navigation)
//   { has_key, has_resume, has_applications, applications_count }
// One call after login decides where the user lands. No Claude, no decryption.
export const getUserState = () => API.get('/user/state');

// Home dashboard (V1-11 — state-aware Home page)
// Returns pipeline counts, recency signal, and personalization snippet
// from the cached resume parse. Cheap — no Claude calls.
export const getHomeDashboard = () => API.get('/home');
